import { FORM_ERROR } from 'final-form';
import { ValidationError as BaseValidationError } from 'yup';
import { xorWith, isEqual, isEmpty } from 'lodash';

/**
 * This validates a yup schema and converts any validation errors to the
 * ValidationErrorSet format that react-final-form expects.
 */
export async function validateSchema(schema, values, options) {
  try {
    await schema.validate(values, { abortEarly: false, ...options });
  } catch (validationError) {
    if (!(validationError instanceof BaseValidationError)) {
      throw validationError;
    }
    return validationError.inner.reduce((errors, error) => {
      if (errors[error.path] == null) {
        errors[error.path] = [];
      }
      errors[error.path].push({ ...error });

      return errors;
    }, {});
  }

  return undefined;
}

/**
 * This validates a single yup type, i.e. string, and converts any validation
 * errors to the ValidationError array format that react-final-form expects.
 */
export async function validateField(schema, value, options) {
  try {
    await schema.validate(value, { abortEarly: false, ...options });
  } catch (validationError) {
    if (!(validationError instanceof BaseValidationError)) {
      throw validationError;
    }
    return validationError.inner.map((error) => ({ ...error }));
  }
  return undefined;
}
/**
 * This validates a single yup type, i.e. string, synchronously and converts any
 * validation errors to the ValidationError array format that react-final-form expects.
 */
export function validateFieldSync(schema, value, options) {
  try {
    schema.validateSync(value, { abortEarly: false, ...options });
  } catch (validationError) {
    if (!(validationError instanceof BaseValidationError)) {
      throw validationError;
    }
    return validationError.inner;
  }
  return undefined;
}

/**
 * Creates a validate function from the given Yup schema that can be used
 * as a prop to final-form's Field component.
 */
export function createFieldValidator(schema, options) {
  return (value) => validateField(schema, value, options);
}

/**
 * Creates a parse function from the given Yup schema that can be used
 * as a prop to final-form's Field component.
 */
export function createFieldParser(schema) {
  return (value) => {
    const castedValue = schema.cast(value);
    if (castedValue === '') {
      return undefined;
    }
    return castedValue;
  };
}

function normalizeGraphQLValidationError(errorSet, error, parentPath) {
  const path = [...parentPath, error.key];
  if (error.messages) {
    const pathString = path.join('.');
    errorSet = {
      ...errorSet,
      [pathString]: (error.messages || []).map((message) => ({
        type: undefined,
        params: undefined,
        message,
      })),
    };
  }
  // eslint-disable-next-line no-use-before-define
  return normalizeGraphQLValidationErrorSet(errorSet, error.errors, path);
}

function normalizeGraphQLValidationErrorSet(errorSet, errors, parentPath) {
  if (errors == null) {
    return errorSet;
  }
  return errors.reduce(
    (newErrorSet, error) => normalizeGraphQLValidationError(newErrorSet, error, parentPath || []),
    errorSet
  );
}

/**
 * This takes an unresolved GraphQL mutation and converts any failed responses
 * to the ValidationErrorSet format that react-final-form expects.
 */
export async function handleMutation(
  mutationPromise,
  mutationKey = 'mutation',
  catchWholeFormErrors
) {
  let response;
  try {
    response = await mutationPromise;
  } catch (error) {
    if (!catchWholeFormErrors) throw error;
    return { [FORM_ERROR]: error };
  }
  const mutation = response.data && response.data[mutationKey];
  if (mutation && mutation.status > 299) {
    return normalizeGraphQLValidationErrorSet({}, mutation.errors, []);
  }
  return undefined;
}

function toArray(errors) {
  if (errors == null) return [];
  if (Array.isArray(errors)) return errors;
  return [errors];
}

function normalizeFieldError(error) {
  if (typeof error === 'string') {
    return { message: error, type: undefined, params: undefined };
  }
  return error;
}

/**
 * Takes just about any input of error(s) and normalizes them to an
 * array of ValidationErrors.
 */
export function normalizeFieldErrors(errors) {
  return toArray(errors).map(normalizeFieldError);
}

/**
 * Returns all of the errors from a react-final-form Field's `meta` prop as
 * a normalied array of ValidationErrors. If the Field has not changed since
 * the last submit and there are submitErrors, those will be returned as well.
 */
export function coalesceFieldErrors(fieldMeta) {
  const { error, submitError, dirtySinceLastSubmit } = fieldMeta;
  const errors = normalizeFieldErrors(error);
  if (dirtySinceLastSubmit) {
    return errors;
  }
  return errors.concat(normalizeFieldErrors(submitError));
}

export function shouldDisplayErrors(fieldMeta, displayErrorState) {
  if (displayErrorState === 'dirty') {
    return !!fieldMeta.dirty;
  }
  return !!fieldMeta.touched;
}

/**
 * Update multiple values in a form using a simple object of name/value pairs.
 */
export function updateValues(form, values) {
  form.batch(() => {
    Object.keys(values).forEach((key) => {
      form.change(key, values[key]);
    });
  });
}

// compare values
export function compareValues(a, b) {
  return isEmpty(xorWith(a, b, isEqual));
}
